import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Card, CardContent, Grid, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import util from '../util/index';
import strings from '../Globalization/index';
import NextPrevComponent from '../components/common/NextPrevComponent';
import CycleInfoProvider from '../contexts/CycleInfo';
import { useCycleInfo } from '../contexts/CycleInfo/Context';
import CycleTape from '../contexts/CycleInfo/CycleTape';
import { usePrerequisites } from '../contexts/Prerequisites';
import { useSetState, useUniqueEffect } from '../hooks';
import CriticalParametersTable from './endosystemcycleinfoviews/CriticalParametersTable';
import TopSection from './endosystemcycleinfoviews/TopSection';
import TrendsLineChart from './endosystemcycleinfoviews/TrendsLineChart';
import { getPrintDetailsParams } from './endosystemcycleinfoviews/dataUtil';

export const CycleInfo = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	let displayCycleTape = false;
	const screenWidth = useMediaQuery(theme.breakpoints.between('sm', 'md'));
	const is1024Width = useMediaQuery('(max-width: 1024px) and (max-height: 768px)');
	if (screenWidth || is1024Width) {
		displayCycleTape = true;
	}
	const { data, cycleTapeDisplayed, onNext, onPrevious, stockChartRef } = useCycleInfo();

	const isPerformanceAccess = useMemo(
		() => util.checkAccess('PerformanceMonitorAccess'),
		[]
	);

	useEffect(() => {
		if (!isPerformanceAccess) {
			Swal.fire({
				titleText: strings.Alert,
				text: strings.AccessError,
				showCancelButton: false,
				confirmButtonText: strings.Ok,
				confirmButtonClass: 'btn-danger',
				closeOnConfirm: true,
				allowOutsideClick: false
			}).then(() => {
				navigate(`/`);
			});
		}
	}, [isPerformanceAccess]);


	return (
		!!data && (
			<Grid container rowSpacing={2} size={12}>
				<Grid size={12} container>
					<Card
						sx={{
							width: '100%',
							'& .MuiCardContent-root:last-child': {
								paddingBottom: '0px',
							}
						}}
					>
						<CardContent>
							<Grid container size={12}>
								<Grid item size={12} >
									<NextPrevComponent onNext={onNext} onPrev={onPrevious}>
										<Box display="flex" columnGap={1} flexDirection={'row'}>
											<Box flexGrow={1}>
												<TopSection />
											</Box>
										</Box>
									</NextPrevComponent>
								</Grid>
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				<Grid size={12} container>
					<Card sx={{ width: '100%' }}>
						<CardContent>
							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns: displayCycleTape ? '1fr' : '1fr auto',
									gridTemplateRows: '1fr',
									gridColumnGap: '20px'
								}}>
								<Box flexGrow={1}>
									<Grid container rowGap={1}>
										<Grid size={12}>
											<CriticalParametersTable />
										</Grid>
										<Grid size={12}>
											<TrendsLineChart ref={stockChartRef} />
										</Grid>
									</Grid>
								</Box>
								{cycleTapeDisplayed && (
									<Box>
										<CycleTape />
									</Box>
								)}
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		)
	);
};

const EndoSystemCycleInfo = () => {
	const [, { navigateToLastAccessibleRoute }] = usePrerequisites();
	const dateTimeFormat = util.getDateTimeFormat();
	const [searchParams, ] = useSearchParams();
	const canReprocessXml = true;
	const isPerformanceAccess = useMemo(() => util.checkAccess('PerformanceMonitorAccess'), []);
	const [state, ] = useSetState({
		processMonitoringValue: false
	});
	;
	const equipmentFamilyId = useMemo(() => searchParams.get('equipmentFamilyId'), [searchParams]);
	const isLiquidChemical = useMemo(() => Boolean(searchParams.get('isLiquidChemical')), [searchParams]);
	const isEndoCabinet = useMemo(
		() => Boolean(searchParams.get('isEndoCabinet')),
		[searchParams]
	);
	const onGetPrintDetailsParams = useCallback(
		({ data }) => getPrintDetailsParams({ data, dateTimeFormat, isEndoCabinet, equipmentFamilyId, isLiquidChemical }),
		[dateTimeFormat, isEndoCabinet, isLiquidChemical, equipmentFamilyId]
	);

	useUniqueEffect(() => {
		if (!isPerformanceAccess) {
			Swal.fire({
				titleText: strings.Alert,
				text: strings.AccessError,
				showCancelButton: false,
				confirmButtonText: strings.Ok,
				confirmButtonClass: 'btn-danger',
				closeOnConfirm: true,
				allowOutsideClick: false
			}).then(() => {
				navigateToLastAccessibleRoute(false);
			});
		}
	}, [isPerformanceAccess]);

	return (
		<CycleInfoProvider
			value={{
				...state,
				isLiquidChemical,
				isEndoCabinet,
				canReprocessXml,
				equipmentFamilyId,

			}}
			type={'endoSystemCycle'}
			onGetPrintDetailsParams={onGetPrintDetailsParams}
		>
			<CycleInfo />
		</CycleInfoProvider>
	);
};

export default EndoSystemCycleInfo;
